import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

import {
  buildExportPoliciesDrawerSections,
  ALL,
  exportPoliciesExcelColumnWidths,
  exportPoliciesExcelHeaders,
  exportPoliciesExcelStyles,
  IExportPoliciesFilters
} from '@common/constants/drawer.constants';
import {
  useGetExportPoliciesLOVs,
  useSearchPolicyPersons,
  useSearchUsersForExport
} from '@common/slices/drawer.slice';
import FormattingService from '@core/services/FormattingService';
import DateService from 'src/core/services/DateService';
import { capitalizeFirstCharLowerOthers } from '@utils/formatting-utils';
import {
  IBaseDrawerProps,
  SearchPolicyPersonsResponse,
  SearchPolicyPersonsVariables
} from '@common/types';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';

const buildExportPoliciesLovs = (lovData: any) => {
  const statusEnum =
    lovData?.PolicyStatusList?.enumValues?.map((e: any) => e.name) || [];
  const businessPartners =
    lovData?.production?.getProposalBusinessPartners || [];
  const currencies = lovData?.core?.getCurrencies || [];

  const Status: Record<string, string> = { [ALL]: 'All' };
  statusEnum.forEach((v: string) => {
    Status[v] = FormattingService.formatEnumToDisplay(v) ?? v;
  });

  const Broker: Record<string, string> = { [ALL]: 'All' };
  businessPartners.forEach((bp: any) => {
    if (bp?.id) {
      Broker[String(bp.id)] = `${bp.code} - ${bp.fullName}`;
    }
  });

  const Currency: Record<string, string> = { [ALL]: 'All' };
  currencies.forEach((c: any) => {
    if (c?.id) {
      Currency[String(c.id)] = String(c.title ?? c.code);
    }
  });

  return { Status, Broker, Currency };
};

const normalizeMulti = (v: any): string[] => {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
};

const fetchAllPoliciesForExport = async (
  searchPolicyPersons: (options?: {
    variables: SearchPolicyPersonsVariables;
  }) => Promise<{ data: SearchPolicyPersonsResponse }>,
  vars: {
    selectedUsers: string[] | null;
    selectedBusinessPartners: string[] | null;
    effectiveOnStart: any | null;
    effectiveOnEnd: any | null;
    expiryOnStart: any | null;
    expiryOnEnd: any | null;
    issueDateStart: any | null;
    issueDateEnd: any | null;
    createdOnStart: any | null;
    createdOnEnd: any | null;
    statuses: string[] | null;
    currencies: string[] | null;
  }
) => {
  const pageSize = 500;
  let currentPage = 1;
  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await searchPolicyPersons({
      variables: {
        currentPage,
        currentPageSize: pageSize,
        Attribute: 'createdOn',
        Descending: true,
        ...vars
      }
    });

    const items = data?.production?.searchPolicyPersons?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    currentPage += 1;
  }

  return all;
};

const getRelationCode = (relation: string | null | undefined): string => {
  const r = String(relation || '').toUpperCase();
  if (r === 'PRINCIPAL') return 'MO';
  if (r === 'SPOUSE') return 'MS';
  if (r === 'CHILD') return 'MD';
  return '';
};

const getLatestInsuredPolicyQuoteTravel = (
  insuredPolicyQuoteTravels: any[] | undefined
) =>
  insuredPolicyQuoteTravels?.find(
    (quote) => quote?.policyVersionState === 'LATEST'
  );

const mapPoliciesToExcelData = (items: any[]) => {
  return items.map((person: any) => {
    const currencySymbol = person?.policy?.policyCurrency?.symbol ?? '';
    const latestTravelQuote = getLatestInsuredPolicyQuoteTravel(
      person?.insuredPolicyQuoteTravels
    );

    return {
      policyNumber: person?.policy?.policyNumber ?? '',
      idNumber: person?.passportNumber ?? '',
      effectiveDate: person?.policy?.policyEffectiveDate ?? '',
      expirationDate: person?.policy?.policyExpiryDate ?? '',
      name: person?.firstName ?? '',
      lastName: person?.lastName ?? '',
      mobilePhone: person?.phoneNumber ?? '',
      email: person?.email ?? '',
      address: person?.address?.addressDetails ?? '',
      city: person?.address?.city ?? '',
      state: '',
      zipCode: '',
      dob: person?.dateOfBirth ?? '',
      contractTier: person?.policy?.plan
        ? `${person.policy.plan.code} - ${person.policy.plan.name}`
        : '',
      relationCode: getRelationCode(person?.relation),
      totalPremium: latestTravelQuote?.coverTotalPremium ?? 0,
      totalPremiumCurrency: currencySymbol,
      status:
        FormattingService.formatEnumToDisplay(
          person?.policy?.policyStatus
        ) ??
        person?.policy?.policyStatus ??
        '',
      relation:
        capitalizeFirstCharLowerOthers(person?.relation ?? '') ||
        FormattingService.formatEnumToDisplay(person?.relation) ||
        '',
      sumInsured: latestTravelQuote?.coverSumInsured ?? '',
      sumInsuredCurrency: currencySymbol
    };
  });
};

const formatDateRange = (range: any[]) => {
  if (!range?.[0] || !range?.[1]) return null;
  return [
    DateService.formatDateTimeBackend(range[0]),
    DateService.formatDateTimeBackend(range[1])
  ];
};

const ExportPoliciesDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const [values, setValues] = useState<IExportPoliciesFilters>({
    user: [ALL],
    broker: [ALL],
    currency: [ALL],
    status: [ALL],
    policyEffectiveDateRange: [],
    policyExpiryDateRange: [],
    policyIssueDateRange: [],
    createdOn: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetExportPoliciesLOVs.useQuery();
  const [searchPolicyPersons] = useSearchPolicyPersons.useLazy();
  const [searchUsers] = useSearchUsersForExport.useLazy();

  useEffect(() => {
    const built = buildExportPoliciesLovs(lovData);
    setLovs(built);
    setSections(buildExportPoliciesDrawerSections(values, built));
  }, [lovData]);

  const submitForm = async (): Promise<void> => {
    const [data] = normalizeDynamicFormDrawerValues(sections, values);
    const selectedUsers = normalizeMulti(data.user);
    const selectedBrokers = normalizeMulti(data.broker);
    const selectedStatuses = normalizeMulti(data.status);
    const selectedCurrencies = normalizeMulti(data.currency);

    const effectiveDateRange = formatDateRange(data.policyEffectiveDateRange);
    const expiryDateRange = formatDateRange(data.policyExpiryDateRange);
    const issueDateRange = formatDateRange(data.policyIssueDateRange);
    const createdOnRange = formatDateRange(data.createdOn);

    const items = await fetchAllPoliciesForExport(searchPolicyPersons, {
      selectedUsers: selectedUsers.includes(ALL) ? null : selectedUsers,
      selectedBusinessPartners: selectedBrokers.includes(ALL)
        ? null
        : selectedBrokers,
      effectiveOnStart: effectiveDateRange?.[0] || null,
      effectiveOnEnd: effectiveDateRange?.[1] || null,
      expiryOnStart: expiryDateRange?.[0] || null,
      expiryOnEnd: expiryDateRange?.[1] || null,
      issueDateStart: issueDateRange?.[0] || null,
      issueDateEnd: issueDateRange?.[1] || null,
      createdOnStart: createdOnRange?.[0] || null,
      createdOnEnd: createdOnRange?.[1] || null,
      statuses: selectedStatuses.includes(ALL) ? null : selectedStatuses,
      currencies: selectedCurrencies.includes(ALL) ? null : selectedCurrencies
    });

    if (!items.length) {
      toast.error(
        <ToastErrorMessage>
          No policies found for the selected filters
        </ToastErrorMessage>
      );

      return;
    }

    const excelData = mapPoliciesToExcelData(items);

    downloadExcelFile({
      data: excelData,
      filename: 'Redbridge_Policies',
      headers: exportPoliciesExcelHeaders,
      sheetName: 'Sheet1',
      styles: exportPoliciesExcelStyles,
      columnWidths: exportPoliciesExcelColumnWidths,
      freezeHeaderRows: 2,
      headerRowHeight: 14,
      groupHeaderRowHeight: 18,
      separator: { enabled: true, applyTo: 'headerOnly', style: 'medium' }
    });
    onSuccess();
  };

  const handleChange = (
    fieldName: keyof IExportPoliciesFilters,
    value: any,
    _sectionIndex: number,
    updatedValues: IExportPoliciesFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(buildExportPoliciesDrawerSections(newValues, lovs));
  };

  const handleAutocompleteSearch = async (
    fieldName: string,
    query: string,
    pagination: { pageNumber: number; pageSize: number }
  ) => {
    if (fieldName === 'user') {
      const result = await searchUsers({
        variables: {
          keyword: query || '',
          pageNumber: pagination.pageNumber,
          pageSize: pagination.pageSize
        },
        fetchPolicy: 'no-cache'
      });
      const items = result?.data?.core?.searchUsers?.items ?? [];
      const options: Record<string, string> = { [ALL]: 'All' };
      items.forEach((u: any) => {
        if (u?.id) {
          const fullName = [u.firstName, u.middleName, u.lastName]
            .filter(Boolean)
            .join(' ');
          options[String(u.id)] = `${u.code ?? ''} - ${fullName}`;
        }
      });

      return { options, totalCount: items.length + 1 };
    }

    const allOptions = lovs[fieldName] ?? {};
    const filtered = Object.fromEntries(
      Object.entries(allOptions).filter(([, label]) =>
        label.toLowerCase().includes(query.toLowerCase())
      )
    );
    const entries = Object.entries(filtered);
    const start = (pagination.pageNumber - 1) * pagination.pageSize;

    return {
      options: Object.fromEntries(
        entries.slice(start, start + pagination.pageSize)
      ),
      totalCount: entries.length
    };
  };

  const handleInitialMultiAutocompleteSearch = async (
    fieldName: string,
    vals: string[]
  ) => {
    if (fieldName === 'user') {
      const options: Record<string, string> = {};
      if (vals.includes(ALL)) options[ALL] = 'All';

      return { options, totalCount: Object.keys(options).length };
    }

    const allOptions = lovs[fieldName] ?? {};
    const options = Object.fromEntries(
      vals.filter((v) => allOptions[v]).map((v) => [v, allOptions[v]])
    );

    return { options, totalCount: Object.keys(options).length };
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
      onAutocompleteSearch={handleAutocompleteSearch}
      initialMultiAutocompleteSearch={handleInitialMultiAutocompleteSearch}
    />
  );
};

export { ExportPoliciesDrawer };
